import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { applyBrand, clearBrand } from "../design/brand";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// The session itself is an httpOnly cookie (unreadable from JS) — this localStorage flag is only a
// HINT that a session was minted on this device, so public marketing pages can skip the /auth/me
// probe for plain guests (the probe's guaranteed 401 polluted every marketing page's console —
// marketing baseline audit 2026-08-02). The hint is advisory: it is set on every successful auth,
// cleared on logout/expiry, and when in doubt (private mode, cleared storage on an app route) we
// still probe — so session restoration inside the app never regresses.
const SESSION_HINT_KEY = "vp-session-hint";
const readSessionHint = () => { try { return localStorage.getItem(SESSION_HINT_KEY) === "1"; } catch { return true; } };
const writeSessionHint = (on) => { try { on ? localStorage.setItem(SESSION_HINT_KEY, "1") : localStorage.removeItem(SESSION_HINT_KEY); } catch { /* private mode */ } };
// Public routes can skip the probe only for a never-authed device. A positive hint still probes,
// so PublicOnly continues to bounce an existing session away from /login or /register.
export const GUEST_SKIP_PATHS = [
  /^\/$/, /^\/solutions(?:\/|$)/, /^\/videos$/, /^\/switch$/, /^\/pricing$/, /^\/modules/, /^\/mobile-app$/, /^\/aira$/, /^\/help$/, /^\/contact$/, /^\/register$/,
  /^\/about$/, /^\/security$/, /^\/compliance\//, /^\/privacy-policy$/, /^\/refund-policy$/, /^\/terms-and-conditions$/,
  /^\/cookie-policy$/, /^\/legal\//, /^\/account-deletion$/,
  // Marketing routes added in the 2026-08-18 audit remediation. A public route MUST be listed here
  // or a plain guest pays a guaranteed-401 /auth/me probe on it (the F-08 console-pollution defect);
  // src/context/guestProbeCoverage.test.js pins every prerendered marketing route against this list.
  /^\/integrations$/, /^\/implementation$/, /^\/support$/, /^\/school-chains$/,
  /^\/resources/, /^\/help\//, /^\/careers/,
];

// Established commercial modules keep the historical fail-open behaviour for an old server session.
// Release-controlled products are different: absence of an explicit feature list must never make a
// not-yet-launched workspace discoverable. Keep this set aligned with backend READINESS_GATED.
const READINESS_GATED_FEATURES = new Set([
  "hr_payroll",
  "workforce_analytics",
  "hr_payroll_ai",
]);
const AUTHORITY_REFRESH_BASE_MS = 60_000;
const AUTHORITY_REFRESH_MAX_MS = 5 * 60_000;

function jitteredRefreshDelay(delayMs) {
  // Keep tenant browsers from producing a synchronized /auth/me spike on the minute boundary.
  // Jitter is deliberately modest so revocation presentation remains prompt; backend requests are
  // independently fail-closed against live authority throughout this window.
  return Math.round(delayMs * (0.85 + Math.random() * 0.3));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated, object = authed
  const [loading, setLoading] = useState(true);
  const authEpochRef = useRef(0);
  const authRequestRef = useRef(null);

  const replaceUser = useCallback((nextUser) => {
    // Invalidate an older /auth/me response before changing identity/session presentation. The
    // request cannot be cancelled reliably across every supported browser, so the epoch is the
    // commit guard and clearing the slot lets the new session refresh independently.
    authEpochRef.current += 1;
    authRequestRef.current = null;
    setUser(nextUser);
  }, []);

  const checkAuth = useCallback(async ({ preserveOnTransientError = false } = {}) => {
    if (authRequestRef.current) return authRequestRef.current;
    const epoch = authEpochRef.current;
    const requestPromise = (async () => {
      try {
        // Session lives in the httpOnly cookie; ask the server who we are.
        const { data } = await api.get("/auth/me");
        writeSessionHint(true);
        if (epoch === authEpochRef.current) setUser(data);
        return data;
      } catch (error) {
        const status = error?.response?.status;
        if (
          epoch === authEpochRef.current &&
          (!preserveOnTransientError || status === 401 || status === 403)
        ) {
          // Only a CONCLUSIVE unauthenticated verdict clears the marketing session hint — a
          // transient network failure during the authority refresh must not make the next load
          // skip the probe (it would strand a real session on a guest-skip path).
          writeSessionHint(false);
          setUser(false);
        }
        return null;
      } finally {
        setLoading(false);
      }
    })();
    authRequestRef.current = requestPromise;
    try {
      return await requestPromise;
    } finally {
      if (authRequestRef.current === requestPromise) authRequestRef.current = null;
    }
  }, []);

  useEffect(() => {
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    if (!readSessionHint() && GUEST_SKIP_PATHS.some((r) => r.test(path))) {
      // Never-authed guest on a marketing page: resolve without the guaranteed-401 network probe.
      setUser(false);
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const refreshTenantId = user && typeof user === "object" ? user.tenant_id : null;

  // Platform release switches are live authority, not login-time decoration. Refresh on focus /
  // tab return and on a bounded cadence so a tenant disable takes effect without requiring logout,
  // while a transient network failure preserves the last authenticated shell. Backend enforcement
  // remains request-fresh during the short presentation refresh window.
  useEffect(() => {
    if (!refreshTenantId) return undefined;
    let stopped = false;
    let timer = null;
    let transientFailures = 0;
    const refresh = async () => {
      if (document.visibilityState !== "hidden") {
        const refreshed = await checkAuth({ preserveOnTransientError: true });
        transientFailures = refreshed ? 0 : Math.min(transientFailures + 1, 3);
        return refreshed;
      }
      return null;
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const onFocus = () => void refresh();
    const schedule = (delayMs) => {
      timer = window.setTimeout(async () => {
        if (stopped) return;
        await refresh();
        if (stopped) return;
        const backoff = Math.min(
          AUTHORITY_REFRESH_BASE_MS * (2 ** transientFailures),
          AUTHORITY_REFRESH_MAX_MS,
        );
        schedule(backoff);
      }, jitteredRefreshDelay(delayMs));
    };
    schedule(AUTHORITY_REFRESH_BASE_MS);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [checkAuth, refreshTenantId]);

  // WL: re-theme the app from the authed tenant's branding (login / session restore); revert on logout.
  useEffect(() => {
    if (user && typeof user === "object") applyBrand(user.branding);
    else if (user === false) clearBrand();
  }, [user]);

  // Step 1: validate the password. The server now requires a SECOND FACTOR — it sends a one-time
  // code to the registered email + mobile and returns { otp_required, masked_*, channels }. NO
  // session is created here. The caller shows the OTP step and calls loginVerify().
  const login = async (email, password, captchaToken, audience = "staff") => {
    const endpoint = audience === "platform" ? "/auth/platform/login" : "/auth/login";
    const { data } = await api.post(endpoint,
      { email, password, captcha_token: captchaToken || undefined });
    return data;  // { otp_required: true, masked_email, masked_mobile, channels }
  };

  // Step 2: verify the code and complete login (mints the session, sets the user).
  const loginVerify = async (email, otp, audience = "staff") => {
    const endpoint = audience === "platform"
      ? "/auth/platform/login/verify"
      : "/auth/login/verify";
    const { data } = await api.post(endpoint, { identifier: email, otp });
    writeSessionHint(true);
    replaceUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    // Verification-queue mode (SCHOOL_APPROVAL_REQUIRED) returns {pending_verification, message}
    // and NO session — only set the user when one was actually minted.
    if (data?.user) { writeSessionHint(true); replaceUser(data.user); }
    return data;
  };

  const googleSession = async (credential, audience = "staff") => {
    const endpoint = audience === "platform"
      ? "/auth/platform/google/session"
      : "/auth/google/session";
    const { data } = await api.post(endpoint, { credential });
    // A first-time school signup can complete through Google while the new tenant still requires
    // Platform verification. This is a successful, deliberately sessionless outcome—not an
    // authenticated user and not an error—so preserve it for the login page's confirmation state.
    if (data?.pending_verification) return data;
    // An MFA-required role (e.g. tenant_admin) gets the mandatory second factor even via Google SSO —
    // the server returns { otp_required, identifier, ... } and NO session yet. The caller shows the OTP
    // step (reusing the password 2FA UI) and completes with loginVerify(identifier, otp).
    if (data.otp_required) return data;
    writeSessionHint(true);
    replaceUser(data.user);
    return data.user;
  };

  const logout = async () => {
    authEpochRef.current += 1;
    authRequestRef.current = null;
    try {
      await api.post("/auth/logout");
    } catch {}
    writeSessionHint(false);
    setUser(false);
  };

  const can = (perm) => {
    if (!user || !user.permissions) return false;
    if (user.permissions.includes("*:*")) return true; // admins — never reducible (WA2.1)
    const [m, a] = perm.split(":");
    // Platform capability ceiling (WA2.1): an explicitly denied capability wins over any grant
    // (incl. wildcards) for non-admin roles, so the UI matches the backend's has_permission.
    const denied = user.denied_capabilities || [];
    if (
      denied.includes(perm) ||
      denied.includes(`${m}:*`) ||
      denied.includes(`*:${a}`) ||
      denied.includes("*:*")
    )
      return false;
    return (
      user.permissions.includes(perm) ||
      user.permissions.includes(`${m}:*`) ||
      user.permissions.includes(`*:${a}`)
    );
  };

  // Tier entitlements (platform-admin governed): a module the tenant's plan includes. Established
  // modules retain the compatibility fail-open for old sessions. Readiness-gated products fail closed
  // until `/auth/me` explicitly reports platform enablement.
  const hasFeature = (feature) => {
    const feats = user?.features;
    if (!Array.isArray(feats)) return !READINESS_GATED_FEATURES.has(feature);
    return feats.includes(feature);
  };

  // Owner/admin gate — authoring rules/flows + other governed actions are tenant_admin-only.
  const isTenantAdmin = !!(user && (user.roles || []).includes("tenant_admin"));

  return (
    <AuthContext.Provider
      value={{ user, setUser: replaceUser, loading, login, loginVerify, register, googleSession, logout, refreshSession: checkAuth, can, hasFeature, isTenantAdmin, formatApiErrorDetail }}
    >
      {children}
    </AuthContext.Provider>
  );
}
